import { useEffect, useState } from 'react';
import { formatDateTime } from '../../../lib/format';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlarmClock, Building2, Eye, Hospital, PhoneCall, UserX } from 'lucide-react';
import {
  Alert, Button, DataTable, Field, FilterBar, IconAction, Input, RowActions, SearchInput, SearchSelect, Select, StatusBadge, Tabs, TextField,
  Textarea, type Tone, useToast,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { usePages } from '../../../lib/usePages';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { Dialog } from '../../../components/Dialog';
import { PageHeader, holds } from '../../../layouts/ConsoleShell';

export interface LeadRow {
  id: string;
  kind: 'COMPANY' | 'PARTNER';
  source: string;
  status: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH';
  organisationName: string;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  city: string | null;
  state: string | null;
  intent: string | null;
  workforceBand: string | null;
  nextFollowUpAt: string | null;
  createdAt: string;
  assignedTo: { id: string; fullName: string; email: string } | null;
  noteCount: number;
  activityCount: number;
}

/**
 * Where the inbox lives in the console that opened it.
 *
 * The same screens are mounted twice — as Operations' home, and inside the
 * Platform console beside Companies and the partner network — so a platform
 * administrator reaches enquiries without switching console. Every link on
 * them is built from this, so a lead opened under Platform stays there.
 */
export function useLeadBase() {
  return useLocation().pathname.startsWith('/admin') ? '/admin/leads' : '/ops/leads';
}

export const STATUS_TONE: Record<string, Tone> = {
  NEW: 'dueSoon', CONTACTED: 'pending', QUALIFIED: 'covered',
  CONVERTED: 'covered', LOST: 'neutral',
};

const age = (iso: string) => {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
};

/**
 * The inbox.
 *
 * Built around the questions an operator opens it with: what is waiting,
 * what is nobody's, and what did somebody promise to chase. The stages are
 * tabs with their counts, so the shape of the pipeline is visible without
 * opening a dropdown. "Nobody owns this" and "a promised call is late" are
 * the two things that need somebody now; each appears as a notice only while
 * it is true, and clicking it shows exactly those rows. Open is the default
 * view, sorted by priority then by age, so the enquiry that has waited three
 * days is not buried under this morning's.
 *
 * Each row answers, in order: who is asking, what they want, whose it is,
 * where it stands and what happens next — so the inbox reads as a list of
 * conversations rather than a filter dashboard.
 *
 * Reading the inbox is wider than working it (Support and Finance hold
 * lead.read), so recording an enquiry is offered only with lead.create.
 */
export function Leads() {
  const nav = useNavigate();
  const base = useLeadBase();
  const { toast } = useToast();
  const { principal, permissions } = useAuth();
  const canCreate = holds(permissions, 'lead.create');
  const [stage, setStage] = useState('OPEN');
  const [filters, setFilters] = useState<Record<string, string | undefined>>({});
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);

  const kind = filters.kind ?? '';
  const owner = (filters.owner ?? '') as '' | 'mine' | 'none';
  const priority = filters.priority ?? '';
  const overdue = filters.followUp === 'overdue';

  // Typing is not a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setQ(term.trim()), 250);
    return () => clearTimeout(t);
  }, [term]);

  const summary = useQuery<{
    byStatus: Record<string, number>; followUpOverdue: number; unassigned: number;
  }>(() => api.get('/leads/summary'), []);
  const counts = summary.data?.byStatus ?? {};
  const openCount = (counts.NEW ?? 0) + (counts.CONTACTED ?? 0) + (counts.QUALIFIED ?? 0);
  const allCount = Object.values(counts).reduce((n, v) => n + v, 0);
  const overdueCount = summary.data?.followUpOverdue ?? 0;

  const query = [
    stage !== 'OPEN' ? `&status=${stage}` : '',
    kind ? `&kind=${kind}` : '',
    owner === 'mine' && principal?.id ? `&assignedToId=${principal.id}` : '',
    owner === 'none' ? '&assignedToId=none' : '',
    overdue ? '&followUp=overdue' : '',
    priority ? `&priority=${priority}` : '',
    q ? `&search=${encodeURIComponent(q)}` : '',
  ].join('');

  const leads = usePages<LeadRow>(
    (cursor) => api.get(`/leads?limit=50${query}${cursor ? `&cursor=${cursor}` : ''}`),
    [query],
  );

  const filtered = Boolean(kind || owner || overdue || q || priority);
  const onlyUnassigned = owner === 'none' && !kind && !q && !overdue && !priority && stage === 'OPEN';
  const onlyOverdue = overdue && !owner && !kind && !q && !priority && stage === 'OPEN';

  return (
    <div className="space-y-4">
      <PageHeader
        className="mb-0"
        title="Leads"
        hint="Enquiries from companies wanting their workforce examined, and from hospitals and labs wanting to join the network."
        actions={canCreate && (
          <Button onClick={() => setAdding(true)}>
            <PhoneCall className="h-3.5 w-3.5" aria-hidden /> New enquiry
          </Button>
        )}
      />

      {/* The two things that need somebody now, each a notice only while it is
          true; a click shows exactly those enquiries. */}
      {stage === 'OPEN' && !filtered && ((summary.data?.unassigned ?? 0) > 0 || overdueCount > 0) && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Needs attention">
          {(summary.data?.unassigned ?? 0) > 0 && (
            <button type="button" onClick={() => setFilters({ owner: 'none' })}
                    className="inline-flex min-h-10 items-center gap-2 rounded-btn border border-warning-600/30 bg-warning-100 px-3 text-small font-medium text-neutral-900 hover:border-warning-600/60">
              <UserX className="h-4 w-4 text-warning-600" aria-hidden />
              {summary.data!.unassigned} with no owner
            </button>
          )}
          {overdueCount > 0 && (
            <button type="button" onClick={() => setFilters({ followUp: 'overdue' })}
                    className="inline-flex min-h-10 items-center gap-2 rounded-btn border border-danger-600/30 bg-danger-100 px-3 text-small font-medium text-neutral-900 hover:border-danger-600/60">
              <AlarmClock className="h-4 w-4 text-danger-600" aria-hidden />
              {overdueCount} follow-up{overdueCount === 1 ? '' : 's'} overdue
            </button>
          )}
        </div>
      )}

      <div className="space-y-3">
        <Tabs
          tabs={STAGES.map(([key, label]) => ({
            key, label,
            // The counts are for the whole inbox. Under a filter they disagreed
            // with the list below them, so they step aside while one is on.
            hint: filtered ? undefined
              : (key === 'OPEN' ? openCount : key === 'ALL' ? allCount : counts[key] ?? 0) || undefined,
          }))}
          active={stage}
          onChange={setStage}
        />

        <FilterBar
          filters={[
            {
              key: 'owner', label: 'Owner',
              options: [
                { value: 'mine', label: 'Assigned to me' },
                { value: 'none', label: 'Unassigned' },
              ],
            },
            {
              key: 'kind', label: 'Type',
              options: [
                { value: 'COMPANY', label: 'Companies' },
                { value: 'PARTNER', label: 'Hospitals and labs' },
              ],
            },
            {
              key: 'priority', label: 'Priority',
              options: [
                { value: 'HIGH', label: 'High' },
                { value: 'MEDIUM', label: 'Medium' },
                { value: 'LOW', label: 'Low' },
              ],
            },
            // Offered while there is something late to find, and kept while
            // it is the filter in force, so its chip can still be removed.
            // With nothing overdue it was a control that could only empty
            // the list.
            ...((overdueCount > 0 || overdue) ? [{
              key: 'followUp', label: 'Follow-up',
              options: [{ value: 'overdue', label: 'Overdue' }],
            }] : []),
          ]}
          value={filters}
          onChange={setFilters}
        >
          <SearchInput
            value={term} onValueChange={setTerm}
            placeholder="Name, contact, phone, email or city" aria-label="Search leads"
            className="w-full sm:w-80"
          />
        </FilterBar>
      </div>

      <DataTable<LeadRow>
        rows={leads.items}
        loading={leads.loading}
        error={leads.error}
        onRetry={leads.reload}
        hasMore={leads.hasMore}
        onLoadMore={leads.loadMore}
        loadingMore={leads.loadingMore}
        rowKey={(l) => l.id}
        onRowClick={(l) => nav(`${base}/${l.id}`)}
        empty={filtered || stage !== 'OPEN'
          ? {
              title: onlyUnassigned ? 'Every open enquiry has an owner'
                : onlyOverdue ? 'No follow-ups are overdue'
                  : 'No matching enquiries',
              hint: filtered
                ? 'Nothing matches the search and filters. Remove a filter to see more.'
                : `No enquiries are ${STAGE_LABEL[stage]?.toLowerCase() ?? 'here'} right now.`,
            }
          : {
              title: 'No open enquiries',
              hint: canCreate
                ? 'Website enquiries arrive here on their own. Record a phone or email enquiry with New enquiry.'
                : 'Website enquiries arrive here on their own.',
              ...(canCreate ? { action: <Button onClick={() => setAdding(true)}>New enquiry</Button> } : {}),
            }}
        columns={[
          {
            // Who is asking: the organisation, then how it arrived and when,
            // then the person to ring. One glance, three lines.
            key: 'who', header: 'Enquiry', alwaysVisible: true,
            cell: (l) => (
              <div className="flex min-w-0 items-start gap-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-input bg-neutral-100 text-neutral-600">
                  {l.kind === 'PARTNER'
                    ? <Hospital className="h-4 w-4" aria-hidden />
                    : <Building2 className="h-4 w-4" aria-hidden />}
                </span>
                <div className="min-w-0">
                  <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="truncate font-medium text-neutral-900">{clean(l.organisationName)}</span>
                    {l.priority === 'HIGH' && <StatusBadge tone="overdue">High priority</StatusBadge>}
                  </div>
                  <div className="truncate text-caption font-normal text-neutral-500" title={formatDateTime(l.createdAt)}>
                    {[l.kind === 'PARTNER' ? 'Hospital / lab' : 'Company', clean(l.city), SOURCE_LABEL[l.source] ?? l.source, age(l.createdAt)]
                      .filter(Boolean).join(' · ')}
                  </div>
                  <div className="truncate text-caption font-normal text-neutral-700">
                    {[clean(l.contactName), l.contactPhone ?? l.contactEmail].filter(Boolean).join(' · ')
                      || <span className="text-neutral-500">No contact recorded</span>}
                  </div>
                </div>
              </div>
            ),
          },
          {
            key: 'need', header: 'Requirement', stackFull: true,
            cell: (l) => (
              <div className="text-caption font-normal">
                {l.intent
                  ? <div className="line-clamp-2 text-neutral-800">{l.intent}</div>
                  : <div className="text-neutral-500">Not captured yet</div>}
                {l.workforceBand && <div className="mt-0.5 text-neutral-500">{l.workforceBand} {l.kind === 'PARTNER' ? 'capacity' : 'workers'}</div>}
              </div>
            ),
          },
          {
            key: 'owner', header: 'Owner',
            cell: (l) => (l.assignedTo
              ? (
                <span className="inline-flex min-w-0 items-center gap-2 text-caption text-neutral-800">
                  <Avatar name={l.assignedTo.fullName} />
                  <span className="truncate">{l.assignedTo.fullName}{l.assignedTo.id === principal?.id ? ' (you)' : ''}</span>
                </span>
              )
              : <StatusBadge tone="dueSoon">Unassigned</StatusBadge>),
          },
          {
            // On a card the badge sits beside the heading, not on a line of its own.
            key: 'state', header: 'Stage', mobileLabel: '',
            cell: (l) => <StatusBadge tone={STATUS_TONE[l.status] ?? 'neutral'}>{STAGE_LABEL[l.status] ?? l.status}</StatusBadge>,
          },
          {
            // What happens next. A promised date, late or not; or, for a
            // lead nobody has promised anything on, the act its stage calls for.
            key: 'next', header: 'Next step',
            // The act the stage calls for, with the promised date under it —
            // both, so a late call is never hidden behind "Make contact".
            cell: (l) => {
              const isOpen = OPEN_STAGES.includes(l.status);
              const act = l.status === 'CONVERTED' ? 'Registered'
                : l.status === 'LOST' ? 'Closed'
                  : !l.assignedTo ? 'Assign an owner'
                    : l.status === 'NEW' ? 'Make contact'
                      : l.status === 'CONTACTED' ? 'Qualify the requirement'
                        : 'Convert it';
              const late = isOpen && !!l.nextFollowUpAt && new Date(l.nextFollowUpAt).getTime() < Date.now();
              return (
                <div className="text-caption font-normal">
                  <div className={isOpen ? 'font-medium text-neutral-900' : 'text-neutral-500'}>{act}</div>
                  {isOpen && (l.nextFollowUpAt
                    ? <div className={`tabular-nums ${late ? 'font-medium text-overdue-600' : 'text-neutral-600'}`}>
                        Follow up {shortDate(l.nextFollowUpAt)}{late ? ' · overdue' : ''}
                      </div>
                    : <div className="text-neutral-500">No follow-up set</div>)}
                </div>
              );
            },
          },
          {
            key: 'actions', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '',
            cell: (l) => (
              <RowActions>
                <IconAction
                  label={`View the enquiry from ${clean(l.organisationName)}`}
                  icon={Eye}
                  onClick={() => nav(`${base}/${l.id}`)}
                />
              </RowActions>
            ),
          },
        ]}
      />

      {adding && (
        <RecordCallDialog
          onClose={() => setAdding(false)}
          onDone={(id, name) => {
            setAdding(false);
            toast('success', `${name} is in the inbox.`);
            summary.reload();
            nav(`${base}/${id}`);
          }}
        />
      )}
    </div>
  );
}

export const STAGE_LABEL: Record<string, string> = {
  NEW: 'New', CONTACTED: 'Contacted', QUALIFIED: 'Qualified', CONVERTED: 'Converted', LOST: 'Lost',
};
const STAGES: Array<[string, string]> = [
  ['OPEN', 'Open'], ['NEW', 'New'], ['CONTACTED', 'Contacted'], ['QUALIFIED', 'Qualified'],
  ['CONVERTED', 'Converted'], ['LOST', 'Lost'], ['ALL', 'All'],
];
const OPEN_STAGES = ['NEW', 'CONTACTED', 'QUALIFIED'];
export const SOURCE_LABEL: Record<string, string> = {
  WEBSITE: 'Website', CHATBOT: 'Chatbot', REFERRAL: 'Referral', EVENT: 'Event',
  OUTBOUND: 'Outbound', PHONE: 'Phone', EMAIL: 'Email', OTHER: 'Other',
};
/** Stray punctuation typed at the end of a name ("da,") is not part of it. */
export const clean = (s?: string | null) => (s ?? '').replace(/[\s,;.·-]+$/u, '').trim();
const shortDate = (iso: string) => new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

export function Avatar({ name }: { name: string }) {
  const letters = name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('');
  return (
    <span aria-hidden className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-micro font-semibold text-primary-700">
      {letters || '?'}
    </span>
  );
}

/** What the API accepts as a phone number (lead.dto `phone`). */
const PHONE = /^[0-9+\-\s()]{6,20}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const phoneError = (v: string) => (v.trim() && !PHONE.test(v.trim()) ? 'Enter a phone number of 6 to 20 digits, spaces or + - ( )' : null);
export const emailError = (v: string) => (v.trim() && !EMAIL.test(v.trim()) ? 'Enter an email address like name@company.com' : null);
export const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Recording an enquiry that came in by phone, email or in person.
 *
 * Laid out as the three things the inbox is built on: who is asking, what
 * they want, and what happens next — owner, priority and the promised
 * follow-up are part of the record from the start rather than folded away
 * as "advanced". Only the organisation's name is required, as the API has it:
 * often that is genuinely all anybody knows yet.
 */
function RecordCallDialog({ onClose, onDone }: {
  onClose: () => void;
  onDone: (id: string, name: string) => void;
}) {
  const { principal, permissions } = useAuth();
  // Listing colleagues needs user.read. Without it the choice is yourself or
  // nobody — offering a name list that cannot load is offering nothing.
  const canAssignOthers = holds(permissions, 'user.read');
  const colleagues = useQuery<{ items: Array<{ id: string; fullName: string; status: string }> }>(
    () => (canAssignOthers
      ? api.get('/admin/platform-users?status=ACTIVE&limit=100')
      : Promise.resolve({ items: [] })),
    [canAssignOthers],
  );
  const others = (colleagues.data?.items ?? []).filter((c) => c.id !== principal?.id);

  const [form, setForm] = useState({
    kind: 'COMPANY',
    source: 'PHONE',
    organisationName: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    city: '',
    workforceBand: '',
    intent: '',
    requirement: '',
    priority: 'MEDIUM',
    nextFollowUpAt: '',
    assignedToId: principal?.id ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const set = (k: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [k]: v }));
  const venue = form.kind === 'PARTNER';

  const errors = {
    organisationName: form.organisationName.trim().length < 2 ? 'Enter the organisation’s name (at least 2 characters)' : null,
    contactPhone: phoneError(form.contactPhone),
    contactEmail: emailError(form.contactEmail),
    nextFollowUpAt: form.nextFollowUpAt && form.nextFollowUpAt < localToday() ? 'Choose today or a later date' : null,
  };
  // Name errors wait for the first save attempt; a malformed phone or email
  // says so as soon as it is typed.
  const shown = { ...errors, organisationName: tried ? errors.organisationName : null };
  const invalid = Object.values(errors).some(Boolean);

  async function submit() {
    setTried(true);
    if (invalid) return;
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        kind: form.kind,
        source: form.source,
        priority: form.priority,
        organisationName: form.organisationName.trim(),
      };
      if (form.contactName.trim()) body.contactName = form.contactName.trim();
      if (form.contactEmail.trim()) body.contactEmail = form.contactEmail.trim().toLowerCase();
      if (form.contactPhone.trim()) body.contactPhone = form.contactPhone.trim();
      if (form.city.trim()) body.city = form.city.trim();
      if (form.workforceBand.trim()) body.workforceBand = form.workforceBand.trim();
      if (form.intent.trim()) body.intent = form.intent.trim();
      if (form.requirement.trim()) body.requirement = form.requirement.trim();
      if (form.assignedToId) body.assignedToId = form.assignedToId;
      if (form.nextFollowUpAt) {
        body.nextFollowUpAt = new Date(`${form.nextFollowUpAt}T09:00:00`).toISOString();
      }

      const created = await api.post<{ id: string; organisationName: string }>(
        '/leads', body, crypto.randomUUID(),
      );
      onDone(created.id, created.organisationName);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  return (
    <Dialog
      title="New enquiry"
      info="Record an enquiry that came by phone, email or in person. Website enquiries arrive on their own."
      onClose={onClose}
      wide
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <span className="text-caption font-normal text-neutral-500">
            <span className="text-danger-600" aria-hidden>*</span> Required
          </span>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button onClick={submit} loading={busy}>Save enquiry</Button>
          </div>
        </div>
      }
    >

      <form className="mt-4 space-y-5" onSubmit={(e) => { e.preventDefault(); void submit(); }} noValidate>
        <section aria-labelledby="enq-who" className="space-y-3">
          <h3 id="enq-who" className="text-caption font-semibold uppercase tracking-wider text-neutral-700">Who is asking</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Enquiry from">
              {(p) => (
                <Select {...p} value={form.kind} onChange={(e) => set('kind')(e.target.value)}>
                  <option value="COMPANY">Company: workforce medicals</option>
                  <option value="PARTNER">Hospital or lab: join the network</option>
                </Select>
              )}
            </Field>
            <Field label="How it came in">
              {(p) => (
                <Select {...p} value={form.source} onChange={(e) => set('source')(e.target.value)}>
                  <option value="PHONE">Phone</option>
                  <option value="EMAIL">Email</option>
                  <option value="REFERRAL">Referral</option>
                  <option value="EVENT">Event</option>
                  <option value="OUTBOUND">Outbound</option>
                  <option value="OTHER">Other</option>
                </Select>
              )}
            </Field>
          </div>
          <TextField
            label={venue ? 'Hospital or lab name' : 'Company name'}
            value={form.organisationName}
            onChange={set('organisationName')}
            required
            autoFocus
            placeholder={venue ? 'e.g. Lifecare Diagnostics' : 'e.g. Vapi Chemicals Pvt Ltd'}
            error={shown.organisationName}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Contact person" value={form.contactName} onChange={set('contactName')}
              placeholder={venue ? 'e.g. Lab director' : 'e.g. HR head'}
            />
            <TextField
              label="Phone" value={form.contactPhone} onChange={set('contactPhone')}
              placeholder="e.g. +91 98765 43210" inputMode="tel" type="tel" error={shown.contactPhone}
            />
            <TextField
              label="Email" value={form.contactEmail} onChange={set('contactEmail')}
              placeholder="e.g. contact@company.com" type="email" error={shown.contactEmail}
            />
            <TextField label="City" value={form.city} onChange={set('city')} placeholder="e.g. Vapi" />
          </div>
        </section>

        <section aria-labelledby="enq-what" className="space-y-3 border-t border-neutral-200 pt-4">
          <h3 id="enq-what" className="text-caption font-semibold uppercase tracking-wider text-neutral-700">What they want</h3>
          <div className="grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <TextField
              label="Requirement in a line" value={form.intent} onChange={set('intent')}
              placeholder={venue ? 'e.g. Wants to run pre-employment panels in Surat' : 'e.g. Annual statutory medicals for the plant'}
            />
            <TextField
              label={venue ? 'Daily capacity' : 'Workforce size'} value={form.workforceBand} onChange={set('workforceBand')}
              placeholder={venue ? 'e.g. 50 a day' : 'e.g. 100–500'}
            />
          </div>
          <Field label="Discussion notes / detailed requirements">
            {(p) => (
              <Textarea
                {...p} rows={3} value={form.requirement}
                onChange={(e) => set('requirement')(e.target.value)}
                placeholder="What was discussed: tests they asked about, sites, timeline, who decides…"
              />
            )}
          </Field>
        </section>

        <section aria-labelledby="enq-next" className="space-y-3 border-t border-neutral-200 pt-4">
          <h3 id="enq-next" className="text-caption font-semibold uppercase tracking-wider text-neutral-700">What happens next</h3>
          <div className="grid gap-3 sm:grid-cols-3">
            <SearchSelect label="Owner" value={form.assignedToId} onChange={set('assignedToId')}
              placeholder="Nobody yet" searchPlaceholder="Search colleagues…"
              options={[
                ...(principal ? [{ value: principal.id, label: `Me (${principal.fullName})` }] : []),
                ...others.map((c) => ({ value: c.id, label: c.fullName })),
                { value: '', label: 'Nobody yet' },
              ]} />
            <Field label="Priority">
              {(p) => (
                <Select {...p} value={form.priority} onChange={(e) => set('priority')(e.target.value)}>
                  <option value="HIGH">High</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="LOW">Low</option>
                </Select>
              )}
            </Field>
            <Field label="Follow up on" error={shown.nextFollowUpAt}>
              {(p) => (
                <Input
                  {...p} type="date" min={localToday()} value={form.nextFollowUpAt}
                  onChange={(e) => set('nextFollowUpAt')(e.target.value)}
                />
              )}
            </Field>
          </div>
        </section>

        {error && <Alert tone="danger">{error}</Alert>}
      </form>
    </Dialog>
  );
}
